import { useContext, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import { AppContext } from '../../App';
import useNotificationStatsQuery, {
  canReceiveNotifications,
  growingNotifications,
  toNotificationReq
} from '../../hooks/useNotificationStatsQuery';
import { Kitchen } from '../../libs';
import { useDocumentMedia } from '../../libs/utils';
import NotificationAlert from '../NotificationAlert';

interface NotificationToastsProps {
  kitchen: Kitchen;
  date: string;
}

/** Owns the notification stats and their toasts; a new count re-renders only this component. */
const NotificationToasts = ({ kitchen, date }: NotificationToastsProps) => {
  const { isMobile } = useDocumentMedia();
  const userPermissions = useContext(AppContext).user.permissions;
  const permissions = useMemo(() => userPermissions ?? [], [userPermissions]);
  const skip = !canReceiveNotifications(permissions);
  const { notificationStats, refetchNotificationStats } = useNotificationStatsQuery({ kitchen, date, skip });
  const shown = useRef(toNotificationReq());

  useEffect(() => {
    if (!skip) {
      void refetchNotificationStats();
    }
  }, [kitchen, date, skip, refetchNotificationStats]);

  useEffect(() => {
    for (const { type, pendingReq } of growingNotifications(shown.current, permissions, notificationStats)) {
      toast.dismiss(type);
      toast(<NotificationAlert type={type} pendingReq={pendingReq} />, {
        className: 'notification-alert',
        hideProgressBar: true,
        closeOnClick: true,
        position: isMobile ? 'bottom-center' : 'bottom-right',
        closeButton: true,
        autoClose: 10_000,
        toastId: type
      });
    }
    shown.current = toNotificationReq(notificationStats);
  }, [date, kitchen, notificationStats, isMobile, permissions]);

  return null;
};

export default NotificationToasts;
